import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, FileText, ListChecks, Megaphone, Calendar, Share2 } from "lucide-react";
import api, { apiError } from "../lib/api";

function McqAttempt({ post }) {
  const [answers, setAnswers] = useState(Array((post.questions || []).length).fill(null));
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (answers.some((a) => a === null)) return toast.error("Answer all questions first");
    try { const r = await api.post(`/posts/${post.id}/attempt`, { answers }); setResult(r.data); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="mt-3 space-y-3">
      {(post.questions || []).map((q, i) => (
        <div key={i} data-testid={`mcq-view-q-${post.id}-${i}`} className="nb-flat p-3" style={{ borderRadius: 12 }}>
          <p className="font-bold text-sm">{i + 1}. {q.q}</p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {q.options.map((o, k) => {
              const picked = answers[i] === k;
              const correct = result && result.correct[i] === k;
              const wrong = result && picked && !correct;
              return (
                <button key={k} type="button" disabled={!!result} data-testid={`mcq-pick-${post.id}-${i}-${k}`}
                  onClick={() => setAnswers(answers.map((a, j) => (j === i ? k : a)))}
                  className="text-left text-sm px-3 py-2 rounded-lg border-2 transition-colors"
                  style={{ borderColor: "#171B33", background: correct ? "#A3E635" : wrong ? "#FCA5A5" : picked ? "#FCD34D" : "#fff" }}>{o}</button>
              );
            })}
          </div>
        </div>
      ))}
      {result
        ? <p data-testid={`mcq-score-${post.id}`} className="nb-badge" style={{ background: "#A3E635", padding: "6px 14px" }}><CheckCircle2 className="w-4 h-4" /> Score {result.score}/{result.total}</p>
        : <button data-testid={`mcq-submit-${post.id}`} onClick={submit} className="nb-btn nb-btn-lime nb-btn-sm">Submit answers</button>}
    </div>
  );
}

const ICON = { mcq: ListChecks, notes: FileText, update: Megaphone };
const BG = { mcq: "#C4B5FD", notes: "#93C5FD", update: "#F2FBE3" };

export default function PostCard({ post: p, user, onShare }) {
  const type = p.type || "update";
  const Icon = ICON[type] || Megaphone;
  return (
    <div data-testid={`post-item-${p.id}`} className="nb p-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="nb-badge capitalize" style={{ background: BG[type], padding: "2px 8px", fontSize: 10 }}><Icon className="w-3 h-3" /> {type === "mcq" ? "MCQ Test" : type}</span>
          <p className="font-display font-extrabold mt-2">{p.title}</p>
          {p.content && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{p.content}</p>}
          {type === "notes" && p.file_url && (
            /\.(png|jpe?g|webp|gif)(\?|$)/i.test(p.file_url) || p.file_url.includes("/api/files/")
              ? <a href={p.file_url} target="_blank" rel="noreferrer" data-testid={`notes-file-${p.id}`}><img src={p.file_url} alt="notes" className="mt-3 max-h-64 rounded-xl border-2 border-[#171B33] object-contain" /></a>
              : <a href={p.file_url} target="_blank" rel="noreferrer" data-testid={`notes-file-${p.id}`} className="nb-btn nb-btn-sm mt-3"><FileText className="w-4 h-4" /> Open notes</a>
          )}
          {type === "mcq" && (user?.role === "student"
            ? <McqAttempt post={p} />
            : <p className="text-xs text-slate-500 mt-2">{(p.questions || []).length} questions · login as a student to attempt this test.</p>)}
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {(p.created_at || "").slice(0, 10)}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {p.link && <a href={p.link} target="_blank" rel="noreferrer" data-testid={`post-link-${p.id}`} className="nb-btn nb-btn-lime nb-btn-sm"><ExternalLink className="w-4 h-4" /> Open Link</a>}
          {user?.role === "student" && <button data-testid={`post-share-${p.id}`} onClick={() => onShare(p.id)} className="nb-btn nb-btn-sm"><Share2 className="w-4 h-4" /> Share (+pts)</button>}
        </div>
      </div>
    </div>
  );
}
